import { useRef, useMemo, useCallback, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _ from "lodash"

import { useVenue } from "providers/venue"
import { useTranslation } from "../../providers/i18n/useTranslation"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"

import { sortFeatureByLocaleKey } from "../../utils/sortFeatureByLocaleKey"
import { getFeatureDetail } from "utils/feature"
import { pushDataLayer } from "utils/googletagmanager/pushDataLayer"

import {
  Q_SUB_CATEGORY_ID,
  Q_ORDINAL,
  Q_PARENT_CATEGORY_ID,
  Q_VENUE,
  GTM_VIEW_DIRECTORY_CATEGORY,
} from "constant"

const DEFAULT_FILTER_VALUE = "ALL"

const DEFAULT_LEVEL_OBJECT = {
  id: "all",
  name: "All Floors",
  value: DEFAULT_FILTER_VALUE,
}

export const useDirectory = () => {
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const navigate = useNavigate()
  const {
    occupants: venueOccupant,
    levels,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    findFeatureById,
  } = useVenue()

  const {
    i18n: { language: chosenLanguage },
  } = useTranslation()
  const [searchParams] = useSearchParams()
  const ref = useRef()

  const defaultVenueFilter = useMemo(
    () => locationVenueId || DEFAULT_FILTER_VALUE,
    [locationVenueId]
  )

  // Query String filters
  const parentCategoryId = useMemo(
    () => searchParams.get(Q_PARENT_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const subCategoryId = useMemo(
    () => searchParams.get(Q_SUB_CATEGORY_ID) || DEFAULT_FILTER_VALUE,
    [searchParams]
  )

  const ordinalFilter = useMemo(() => {
    const ordinalParam = searchParams.get(Q_ORDINAL)
    const ordinal = Number(ordinalParam)
    return !_.isNil(ordinalParam) && !_.isNaN(ordinal)
      ? ordinal
      : DEFAULT_FILTER_VALUE
  }, [searchParams])

  // Filter occupants with an available anchor
  const availableOccupants = useMemo(() => {
    return venueOccupant.filter(({ properties }) => _.get(properties, "anchor"))
  }, [venueOccupant])

  // Filter occupants with a parentCategoryId.
  const occupantsFilteredByParentCategory = useMemo(() => {
    return availableOccupants.filter(({ properties }) => {
      const isMatchedCategoryId = _.get(
        properties,
        "local_parent_categories",
        []
      )
        .map(({ id }) => id)
        .includes(parentCategoryId)
      return (
        parentCategoryId === DEFAULT_FILTER_VALUE ||
        (parentCategoryId && isMatchedCategoryId)
      )
    })
  }, [availableOccupants, parentCategoryId])

  const occupantsFilteredBySubCategory = useMemo(
    () =>
      occupantsFilteredByParentCategory.filter(
        ({ properties }) =>
          subCategoryId === DEFAULT_FILTER_VALUE ||
          properties.local_categories
            .map((category) => category.id)
            .includes(subCategoryId)
      ),
    [subCategoryId, occupantsFilteredByParentCategory]
  )

  const occupantsFilteredByVenue = useMemo(
    () =>
      occupantsFilteredByParentCategory.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue.id === venueFilter
      ),
    [venueFilter, occupantsFilteredByParentCategory]
  )

  const occupantsFilteredByLevel = useMemo(
    () =>
      occupantsFilteredByParentCategory.filter(
        ({ properties }) =>
          ordinalFilter === DEFAULT_FILTER_VALUE ||
          properties.ordinal === ordinalFilter
      ),
    [ordinalFilter, occupantsFilteredByParentCategory]
  )

  const filteredOccupantsByCategoryAndVenue = useMemo(() => {
    return _.intersectionBy(
      occupantsFilteredByVenue,
      occupantsFilteredBySubCategory,
      "id"
    )
  }, [occupantsFilteredByVenue, occupantsFilteredBySubCategory])

  const filteredOccupantsResult = useMemo(() => {
    return _.intersectionBy(
      filteredOccupantsByCategoryAndVenue,
      occupantsFilteredByLevel,
      "id"
    )
  }, [filteredOccupantsByCategoryAndVenue, occupantsFilteredByLevel])
  //Occupant to display in a list filtered by ordinal category and building, sorted alphabetically
  const filteredOccupantsSorted = useMemo(() => {
    // The fallback language for sorting can be inserted as the fourth parameter (defaults to "en")
    const sortedOccupant = sortFeatureByLocaleKey(
      filteredOccupantsResult,
      "properties.name",
      chosenLanguage
    )
    // Sort occupants by featured status
    return _.orderBy(sortedOccupant, ["properties.is_featured"], ["desc"])
  }, [filteredOccupantsResult, chosenLanguage])

  const parentCategoryWithOccupant = useMemo(
    () =>
      _(availableOccupants)
        .map("properties.local_parent_categories")
        .compact()
        .flatten()
        .uniqBy("id")
        .value(),
    [availableOccupants]
  )
  const sortedParentCategoryWithOccupant = useMemo(
    () =>
      _.orderBy(
        parentCategoryWithOccupant,
        ["properties.is_featured", "properties.order"],
        ["desc", "asc"]
      ),
    [parentCategoryWithOccupant]
  )
  const subCategoryWithActiveParentCategory = useMemo(
    () =>
      _.isNil(parentCategoryId)
        ? []
        : _(occupantsFilteredByParentCategory)
            .map("properties.local_categories")
            .compact()
            .flatten()
            .uniqBy("id")
            .filter(
              (category) =>
                _.get(category, "properties.parent_id") === parentCategoryId
            )
            .value(),
    [parentCategoryId, occupantsFilteredByParentCategory]
  )
  // Needs to Change name
  const filteredVenues = useMemo(() => {
    return sortFeatureByLocaleKey(venues, "properties.name", chosenLanguage)
  }, [chosenLanguage, venues])

  const venueOptions = useMemo(() => {
    const defaultVenueObject = {
      id: "all-venues",
      name: "All Venues",
      value: DEFAULT_FILTER_VALUE,
    }
    const options = filteredVenues.map((venue) => {
      const { id, name } = getFeatureDetail(venue)
      return { id, name, value: id }
    })

    return [defaultVenueObject, ...options]
  }, [filteredVenues])

  // Array of unique levels of occupants who matched with category
  const filteredLevels = useMemo(
    () =>
      _(filteredOccupantsByCategoryAndVenue)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find((level) => level.properties.ordinal === ordinal)
        )
        .orderBy("properties.ordinal", "asc")
        .value(),
    [levels, filteredOccupantsByCategoryAndVenue]
  )

  const levelOptions = useMemo(() => {
    const options = filteredLevels.map((level) => {
      const { id, name, ordinal } = getFeatureDetail(level)
      return { id, name, value: ordinal }
    })

    return [DEFAULT_LEVEL_OBJECT, ...options]
  }, [filteredLevels])

  const scrollToRef = useCallback(
    () => ref.current?.scrollIntoView({ behavior: "smooth" }),
    [ref]
  )

  const handleChangeOrdinal = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_ORDINAL, id)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate]
  )

  const changeVenueFilter = useCallback(
    (venueId = defaultVenueFilter) => {
      searchParams.set(Q_VENUE, venueId)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
      scrollToRef()
    },
    [scrollToRef, searchParams, navigate, defaultVenueFilter]
  )

  const handleSelectLevelChange = useCallback(
    (e) => {
      const ordinal = e.target.value
      handleChangeOrdinal(e, ordinal)
    },
    [handleChangeOrdinal]
  )

  const handleChangeParentCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_PARENT_CATEGORY_ID, id)
      searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const handleChangeSubCategory = useCallback(
    (e, id) => {
      if (id === null) return
      searchParams.set(Q_SUB_CATEGORY_ID, id)
      searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
      navigate(`/directory?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const resetFilter = useCallback(() => {
    searchParams.set(Q_PARENT_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_SUB_CATEGORY_ID, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_VENUE, DEFAULT_FILTER_VALUE)
    searchParams.set(Q_ORDINAL, DEFAULT_FILTER_VALUE)
    navigate(`/directory?${searchParams.toString()}`, { replace: true })
  }, [navigate, searchParams])

  const showSubCategoryFilter = useMemo(
    () =>
      subCategoryWithActiveParentCategory.length > 0 &&
      parentCategoryId !== "ALL",
    [subCategoryWithActiveParentCategory, parentCategoryId]
  )

  useEffect(() => {
    try {
      if (!!parentCategoryId && parentCategoryId !== DEFAULT_FILTER_VALUE) {
        const categoryName = parentCategoryWithOccupant.find(
          (taxonomy) => taxonomy.id === parentCategoryId
        )?.properties?.name?.en
        return pushDataLayer({
          event: GTM_VIEW_DIRECTORY_CATEGORY,
          directory_category_id: parentCategoryId,
          directory_category_name: categoryName,
          directory_subcategory_name:
            subCategoryId === DEFAULT_FILTER_VALUE
              ? DEFAULT_FILTER_VALUE
              : _.get(findFeatureById(subCategoryId), "properties.name.en"),
        })
      }
    } catch (err) {
      console.log(err)
    }
  }, [
    parentCategoryId,
    parentCategoryWithOccupant,
    subCategoryId,
    findFeatureById,
  ])

  return {
    handleChangeParentCategory,
    handleChangeSubCategory,
    handleChangeOrdinal,
    handleSelectLevelChange,
    changeVenueFilter,
    resetFilter,
    filteredOccupantsSorted,
    filteredLevels,
    filteredVenues,
    parentCategoryWithOccupant,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    ordinalFilter,
    venueFilter,
    levelOptions,
    venueOptions,
    defaultOrdinalFilterValue: DEFAULT_FILTER_VALUE,
    defaultCategoryFilterValue: DEFAULT_FILTER_VALUE,
    defaultVenueFilterValue: DEFAULT_FILTER_VALUE,
    ref,
    venues,
    isMultiVenueProject,
    isMultiOrdinalVenue,
    locationVenueId,
    showSubCategoryFilter,
  }
}
